import {
  createBrowserAudioContext,
  resumeAudioContext,
  type ParkAudioContext,
  type ParkBufferSource,
  type ParkFilter,
  type ParkGain,
} from './context';
import { createMediaAmbienceChannel, type MediaAmbienceHandle } from './mediaAmbience';
import { canPlayAmbience, isSoundMuted, subscribeAudioSettings } from './soundSettings';
import { AUDIO_USER_GESTURE_EVENTS, subscribeUserGestureUnlock } from './userGestureUnlock';

/** Original in-code park bed, complemented by rights-safe Pixabay ambience layers. */
export const AMBIENT_ASSET_STATUS = 'PROCEDURAL_ORIGINAL' as const;
export const AMBIENT_LOOP_URL: string | null = null;
export const ATTRACTION_ACCENT_MIN_INTERVAL_MS = 12_000;
export const ATTRACTION_ACCENT_FLOOR_INTERVAL_MS = 8_000;

/**
 * COL-012A-C2: C1's 160 Hz / 0.008 brown bed was below laptop/phone speaker range.
 * Warm mid band + normalized buffer so the loop is audible but still background.
 * COL-012A-C3: product owner approved exactly 50% of C2 (`0.048 -> 0.024`).
 * COL-012A-C4: another 25% cut (`0.024 -> 0.018`).
 * No oscillator tones. No scheduled chirps.
 */
export const BED_GAIN = 0.018;
export const HIGHPASS_HZ = 150;
export const LOWPASS_HZ = 820;
export const NOISE_SECONDS = 28;
export const BED_TARGET_RMS = 0.18;

export interface AmbientHandle {
  muted: boolean;
  playing: boolean;
  assetStatus: typeof AMBIENT_ASSET_STATUS;
  setMuted(muted: boolean): void;
  stop(): void;
}

export const AMBIENT_USER_GESTURE_EVENTS = AUDIO_USER_GESTURE_EVENTS;

type AmbientHost = {
  handles: number;
  outputMuted: boolean;
  unlocked: boolean;
  ctx: ParkAudioContext | null;
  resolved: boolean;
  source: ParkBufferSource | null;
  highpass: ParkFilter | null;
  filter: ParkFilter | null;
  gain: ParkGain | null;
  started: boolean;
  media: MediaAmbienceHandle | null;
  createContext: () => ParkAudioContext | null;
};

let host: AmbientHost | null = null;
let lastAccentAt = 0;
let unsubscribeSettings: (() => void) | null = null;
let unbindUserGestures: (() => void) | null = null;

function getHost(): AmbientHost {
  if (!host) {
    host = {
      handles: 0,
      outputMuted: isSoundMuted(),
      unlocked: false,
      ctx: null,
      resolved: false,
      source: null,
      highpass: null,
      filter: null,
      gain: null,
      started: false,
      media: null,
      createContext: createBrowserAudioContext,
    };
  }
  return host;
}

function ensureContext(target: AmbientHost, createContext: () => ParkAudioContext | null): ParkAudioContext | null {
  if (target.resolved) {
    return target.ctx;
  }
  target.resolved = true;
  try {
    target.ctx = createContext();
  } catch {
    target.ctx = null;
  }
  return target.ctx;
}

function measureRms(data: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < data.length; i += 1) {
    const sample = data[i] ?? 0;
    sum += sample * sample;
  }
  return Math.sqrt(sum / Math.max(1, data.length));
}

/**
 * Pink-leaning warm bed with slow baked undulation. No oscillator tones.
 * Normalized so device speakers can actually reproduce it after the band filters.
 */
export function fillAmbientBed(data: Float32Array): void {
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let mid = 0;
  const n = data.length;
  const cycleA = 2 + Math.floor(Math.random() * 3);
  const cycleB = 1 + Math.floor(Math.random() * 2);
  const phaseA = Math.random() * Math.PI * 2;
  const phaseB = Math.random() * Math.PI * 2;
  for (let i = 0; i < n; i += 1) {
    const white = Math.random() * 2 - 1;
    b0 = 0.997 * b0 + white * 0.029;
    b1 = 0.985 * b1 + white * 0.042;
    b2 = 0.9 * b2 + white * 0.055;
    mid = mid * 0.91 + white * 0.09;
    const t = i / n;
    const env =
      0.78 +
      0.14 * (0.5 + 0.5 * Math.sin(t * cycleA * Math.PI * 2 + phaseA)) +
      0.08 * (0.5 + 0.5 * Math.sin(t * cycleB * Math.PI * 2 + phaseB));
    data[i] = (b0 + b1 + b2 + mid * 0.35) * env;
  }

  const rms = measureRms(data);
  const scale = rms > 0.0001 ? BED_TARGET_RMS / rms : 0;
  for (let i = 0; i < n; i += 1) {
    data[i] = (data[i] ?? 0) * scale;
  }

  const fade = Math.max(1, Math.floor(n * 0.003));
  for (let i = 0; i < fade; i += 1) {
    const weight = i / fade;
    const start = data[i] ?? 0;
    const endIndex = n - fade + i;
    const end = data[endIndex] ?? 0;
    data[endIndex] = end * (1 - weight) + start * weight;
  }
}

function connectBed(ctx: ParkAudioContext, target: AmbientHost): boolean {
  if (target.started && target.source && target.gain) {
    return true;
  }
  try {
    const length = Math.max(1, Math.floor((ctx.sampleRate || 44_100) * NOISE_SECONDS));
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate || 44_100);
    fillAmbientBed(buffer.getChannelData(0));

    const source = ctx.createBufferSource();
    const highpass = ctx.createBiquadFilter();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    source.buffer = buffer;
    source.loop = true;
    highpass.type = 'highpass';
    highpass.frequency.setValueAtTime(HIGHPASS_HZ, ctx.currentTime);
    highpass.Q.setValueAtTime(0.5, ctx.currentTime);
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(LOWPASS_HZ, ctx.currentTime);
    filter.Q.setValueAtTime(0.55, ctx.currentTime);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    source.connect(highpass);
    highpass.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    source.start(ctx.currentTime);

    target.source = source;
    target.highpass = highpass;
    target.filter = filter;
    target.gain = gain;
    target.started = true;
    return true;
  } catch {
    target.source = null;
    target.highpass = null;
    target.filter = null;
    target.gain = null;
    target.started = false;
    return false;
  }
}

function desiredBedGain(target: AmbientHost): number {
  if (target.outputMuted || !canPlayAmbience()) {
    return 0.0001;
  }
  return BED_GAIN;
}

function applyBedGain(target: AmbientHost): void {
  if (!target.ctx || !target.gain) {
    return;
  }
  try {
    target.gain.gain.setValueAtTime(desiredBedGain(target), target.ctx.currentTime);
  } catch {
    // Graph may already be torn down.
  }
}

function isBedAudible(target: AmbientHost): boolean {
  return target.started && !target.outputMuted && canPlayAmbience() && Boolean(target.ctx);
}

function syncHost(target: AmbientHost, createContext: () => ParkAudioContext | null): void {
  if (target.outputMuted || !canPlayAmbience()) {
    applyBedGain(target);
    return;
  }
  // Do not construct AudioContext before a real user gesture. Boot-time
  // construction stays suspended, and later passive/document resume is ignored.
  if (!target.unlocked) {
    return;
  }
  const ctx = ensureContext(target, createContext);
  if (!ctx) {
    return;
  }
  resumeAudioContext(ctx);
  if (connectBed(ctx, target)) {
    applyBedGain(target);
  }
}

function disposeHost(target: AmbientHost): void {
  try {
    target.source?.stop();
  } catch {
    // Source may already have ended.
  }
  try {
    target.source?.disconnect();
    target.highpass?.disconnect();
    target.filter?.disconnect();
    target.gain?.disconnect();
  } catch {
    // Graph may already be torn down.
  }
  target.source = null;
  target.highpass = null;
  target.filter = null;
  target.gain = null;
  target.started = false;
  target.media?.stop();
  target.media = null;
}

function bindSettings(): void {
  if (unsubscribeSettings) {
    return;
  }
  unsubscribeSettings = subscribeAudioSettings(() => {
    if (!host) {
      return;
    }
    syncHost(host, host.createContext);
  });
}

function unbindUserGestureUnlock(): void {
  if (!unbindUserGestures) {
    return;
  }
  unbindUserGestures();
  unbindUserGestures = null;
}

function bindUserGestureUnlock(): void {
  if (unbindUserGestures) {
    return;
  }
  unbindUserGestures = subscribeUserGestureUnlock(() => {
    nudgeAmbientResume();
  });
}

export function attractionAccentIntervalMs(activeAttractions: number): number {
  const extra = Math.max(0, activeAttractions - 1);
  return Math.max(ATTRACTION_ACCENT_FLOOR_INTERVAL_MS, ATTRACTION_ACCENT_MIN_INTERVAL_MS - extra * 800);
}

export function createAmbientChannel(options?: {
  createContext?: () => ParkAudioContext | null;
}): AmbientHandle {
  const target = getHost();
  target.handles += 1;
  if (!target.media) {
    target.media = createMediaAmbienceChannel();
  }
  const createContext = options?.createContext ?? createBrowserAudioContext;
  target.createContext = createContext;
  bindSettings();
  bindUserGestureUnlock();
  target.outputMuted = isSoundMuted();
  target.media.setMuted(target.outputMuted);
  if (target.unlocked) {
    syncHost(target, createContext);
  }

  const handle: AmbientHandle = {
    muted: target.outputMuted,
    get playing() {
      return host === target && isBedAudible(target);
    },
    assetStatus: AMBIENT_ASSET_STATUS,
    setMuted(muted: boolean) {
      target.outputMuted = muted;
      handle.muted = muted;
      target.media?.setMuted(muted);
      syncHost(target, createContext);
    },
    stop() {
      if (target.handles > 0) {
        target.handles -= 1;
      }
      if (target.handles <= 0) {
        disposeHost(target);
        host = null;
        unbindUserGestureUnlock();
      }
    },
  };
  return handle;
}

export function nudgeAmbientResume(): void {
  if (!host) {
    return;
  }
  // Mark unlock even while muted so a later explicit unmute can start the bed
  // on the same gesture family without requiring another Sound-only click.
  host.unlocked = true;
  if (host.outputMuted || !canPlayAmbience()) {
    return;
  }
  // First eligible click/tap/key must create/resume/start the bed.
  // Do not require a mute cycle; do not invent a pre-gesture autoplay bypass.
  syncHost(host, host.createContext);
}

export function resetAttractionAccentForTests(): void {
  lastAccentAt = 0;
}

export function resetAmbientHostForTests(): void {
  if (host) {
    disposeHost(host);
    host = null;
  }
  unbindUserGestureUnlock();
  if (unsubscribeSettings) {
    unsubscribeSettings();
    unsubscribeSettings = null;
  }
  lastAccentAt = 0;
}

/**
 * Optional later verified clip hook. Rate-limited.
 * COL-012A-C1: no default oscillator accent. Without `play`, this is a no-op.
 */
export function maybePlayAttractionAccent(
  nowMs: number,
  activeAttractions: number,
  play?: () => void,
): boolean {
  if (!canPlayAmbience() || activeAttractions <= 0 || !play) {
    return false;
  }
  if (lastAccentAt !== 0 && nowMs - lastAccentAt < attractionAccentIntervalMs(activeAttractions)) {
    return false;
  }
  lastAccentAt = nowMs;
  play();
  return true;
}
